import React, { Fragment, ReactNode } from "react";
import {
  formatAreas,
  formatBaseTime,
  formatBoolean,
  formatDirection,
  formatFrequency,
  formatHindcastDay, formatLevelist,
  formatResolution,
  formatRun
} from "../../utils/Format";
import { makeResolution } from "../../entities/Resolution";
import { castArray, isEmpty } from "lodash";
import TimeStep from "../ProductFormatters/TimeStep";
import Run from "../ProductFormatters/Run";
import { Col, Container, Row } from "react-bootstrap";
import ParamQuantile from "../ProductFormatters/ParamQuantile";
import { selectParameters } from "../../store/selectors/new/parameters";
import { useSelector } from "react-redux";
import ParamLevelist from "../ProductFormatters/ParamLevelist";
import Steps from "../ProductFormatters/Steps";
import { TBcType } from "../../api/endpoints/Subset";
import { IRealtimeOrderProductData } from "../../api/endpoints/OrderProduct";
import EnsembleMembers from "../ProductFormatters/EnsembleMembers";

interface IProps {
  productData: IRealtimeOrderProductData;
  set: {
    name: string;
  };
  bcType: TBcType;
  timestamp?: string;
  actions?: ReactNode;
}

const ProductAttribute = (props: {
  children: ReactNode;
  label: string;
}) => {
  const { children, label } = props;

  return (
    <Row>
      <Col sm={12} md={3} className="product-attribute-label">
        {label}
      </Col>
      <Col sm={12} md={9} className="product-attribute-values">
        {children}
      </Col>
    </Row>
  );
};

const ProductDetails = (props: IProps) => {
  const { productData, actions, set, bcType, timestamp } = props;
  const legs = productData.legs ?? [];
  const availableParams = useSelector(selectParameters);

  return (
    <Container fluid className="product-details">
      <Row className="product-details-header">
        <Col sm={12} md={9} className="header-info">
          <div className="product-title">{productData.name ? productData.name : <em>(no name)</em>}</div>
          <div className="table__details--item">{set.name}</div>
        </Col>

        {
          actions &&
          <Col sm={12} md={3} className="actions">
            {actions}
          </Col>
        }
      </Row>

      <div className="product-details-content">
        {
          productData.time &&
          <ProductAttribute label="Base times">
            {productData.time.map(formatBaseTime).join(' ')}
          </ProductAttribute>
        }

        {
          legs.map((leg, index) => {
            return (
              <Fragment key={index}>
                {
                  legs.length > 1 &&
                  <strong>Leg {index + 1}</strong>
                }

                {
                  leg.grid &&
                  <ProductAttribute label="Grid">
                    {formatResolution(makeResolution(leg.grid))}
                  </ProductAttribute>
                }

                {
                  leg.step &&
                  <ProductAttribute label="Forecast time-steps">
                    <Steps values={leg.step} bcType={bcType} selectedTimes={productData.time} timestamp={timestamp} />
                  </ProductAttribute>
                }
              </Fragment>
            );
          })
        }

        {
          productData.grid &&
          <ProductAttribute label="Grid">
            {formatResolution(makeResolution(productData.grid))}
          </ProductAttribute>
        }

        {
          productData.step &&
          <ProductAttribute label="Forecast time-steps">
            <Steps values={productData.step} bcType={bcType} selectedTimes={productData.time} />
          </ProductAttribute>
        }

        {
          productData.number &&
          <ProductAttribute label="Ensemble members">
            <EnsembleMembers values={productData.number} as="div" />
          </ProductAttribute>
        }

        {
          !isEmpty(productData.area) &&
          <ProductAttribute label="Areas">
            {
              castArray(formatAreas(productData.area)).map(area => <div key={area}>{area}</div>)
            }
          </ProductAttribute>
        }

        {
          productData.location &&
          <ProductAttribute label="Number of points">
            {
              productData.location
            }
          </ProductAttribute>
        }

        {
          productData.time_step &&
          <ProductAttribute label="Base times">
            <TimeStep values={productData.time_step} />
          </ProductAttribute>
        }

        {
          productData.param &&
          <ProductAttribute label="Parameters">
            {productData.param.join(', ')}
          </ProductAttribute>
        }

        {
          productData.levelist &&
          <ProductAttribute label="Levels">
            {formatLevelist(productData.levelist)}
          </ProductAttribute>
        }

        {
          productData.param_levelist &&
          <ProductAttribute label="Parameters">
            <ParamLevelist value={productData.param_levelist} params={availableParams} />
          </ProductAttribute>
        }

        {
          productData.param_quantile &&
          <ProductAttribute label="Parameters">
            <ParamQuantile value={productData.param_quantile} params={availableParams} />
          </ProductAttribute>
        }

        {
          productData.use &&
          <ProductAttribute label="Runs">
            {
              productData.use.map(formatRun).join(', ')
            }
          </ProductAttribute>
        }

        {
          productData.hindcast && productData.hindcast.length &&
          <ProductAttribute label="Hindcast">
            {productData.hindcast.map(formatHindcastDay).join(', ')}
          </ProductAttribute>
        }

        {
          productData.use_step &&
          <ProductAttribute label="Runs">
            <Run values={productData.use_step} as="div" />
          </ProductAttribute>
        }

        {
          productData.fcmonth &&
          <ProductAttribute label="Months">
            {productData.fcmonth.join(' ')}
          </ProductAttribute>
        }

        {
          productData.channel &&
          <ProductAttribute label="Channels">
            {productData.channel.join(' ')}
          </ProductAttribute>
        }

        {
          productData.direction &&
          <ProductAttribute label="Directions">
            {productData.direction.map(formatDirection).join(', ')}
          </ProductAttribute>
        }

        {
          productData.frequency &&
          <ProductAttribute label="Frequencies">
            {productData.frequency.map(formatFrequency).join(', ')}
          </ProductAttribute>
        }
      </div>
    </Container>
  )
}

export default ProductDetails;
